






import { Component, Prop, Vue } from 'vue-property-decorator';
import { UnitSystem } from '@/interfaces/units';
import { IHistogramBin } from '@/modules/perRoad/lib/calculations/horizontalStrainDistribution';

const echarts = require('echarts');

@Component({
  components: {},
})
export default class PrSurfaceDeflectionPlot extends Vue {
  @Prop() threshold!: number;

  @Prop() targetPercentile!: number;

  @Prop({ default: 0 }) externalKey!: number;

  @Prop() id!: number;

  @Prop() unitSystem!: UnitSystem;

  @Prop({ default: null }) binnedResults!: IHistogramBin[];

  plotKeyInternal = 0;

  get plotKey() {
    return this.plotKeyInternal + this.externalKey;
  }

  get unitSystemIsImperial() {
    return this.unitSystem === UnitSystem.Imperial;
  }

  sync() {
    this.$emit('sync');
    this.plotKeyInternal += 1;
  }

  mounted() {
    this.createPlot();
    this.updatePlot();
  }

  updated() {
    this.createPlot();
    this.updatePlot();
  }

  get plotId() {
    return `vertical-deflection-plot-${this.id}`;
  }

  get xMax() {
    let limit = 0.001;

    if (!this.unitSystemIsImperial) {
      limit *= 2.54;
    }

    if (this.binnedResults) {
      const upperBound = Math.max(...this.binnedResults.map((bin) => bin.upperBound));
      limit = upperBound;
    }

    if (this.threshold > limit) {
      return this.threshold * 1.25;
    }
    return limit;
  }

  get deflectionUnit() {
    return this.unitSystemIsImperial ? 'in' : 'cm';
  }

  chart: any = null;

  createPlot() {
    if (this.chart === null) {
      const el = document.getElementById(this.plotId);
      this.chart = echarts.init(el );
    } else {
      this.chart.dispose();
      this.chart = null;
      const el = document.getElementById(this.plotId);
      this.chart = echarts.init(el );
    }

    this.chart.setOption(
      {
        grid: {
          top: 5,
          right: 5,
          bottom: 45,
          left: 45,
          show: true,
          borderWidth: 1,
          borderColor: '#000',
        },
        tooltip: {},
        graphic: {
          elements: [
            {
              type: 'text',
              position: [180, 150],
              style: {
                text: 'Region of\nacceptable\nperformance',
                textAlign: 'center',
              },

            },
          ],
        },
        xAxis: {
          scale: false,
          min: 0,
          max: this.xMax,
          splitLine: {
            show: false,
          },
          axisLabel: {
            show: true,
          },
          axisLine: {
            show: false,
          },
          name: `Deflection (${this.deflectionUnit})`,
          nameLocation: 'middle',
          nameGap: 30,
        },
        yAxis: {
          name: 'Cumulative %',
          nameLocation: 'middle',
          nameGap: 30,
          min: 0,
          max: 100,
          axisLabel: {
            show: true,
          },
          axisLine: {
            show: false,
          },
          axisTick: {
            location: 'left',
            show: true,
            alignWithLabel: true,
          },
          splitLine: {
            show: false,
          },
        },
        series: [
          {
            type: 'line',
            name: 'Threshold',
            data: null,
            animation: false,
            symbol: 'none',
            tooltip: {
              trigger: 'axis',
              formatter(obj: any) {
                return `<div>${obj[0].seriesName}:<br> ${obj[0].value[0].toFixed(0)} μStrain</div>`;
              },
            },
          },
          {
            type: 'line',
            lineStyle: {},
            name: 'Deflection Result',
            data: null,
            animation: false,
          },
          {
            type: 'line',
            name: 'goodArea',
            symbol: 'none',
            data: null,
            tooltip: {
              formatter(obj: any) {
                return `<div>${obj[0].seriesName}:<br> ${obj[0].value[0].toFixed(0)} μStrain</div>`;
              },
            },
            lineStyle: {
              width: 0,
            },
            areaStyle: {
              origin: 'start',
              color: 'blue',
              opacity: 0.1,
            },
            animation: false,
          },
          {
            type: 'line',
            name: 'failArea',
            symbol: 'none',
            data: null,
            lineStyle: {
              width: 0,
            },
            areaStyle: {
              origin: 'end',
              color: 'red',
              opacity: 0.3,
            },
            animation: false,
          },

        ],
      },
    );
  }


  updatePlot() {
    // Boundary line
    const thresholdLine = [
      [this.threshold, 100],
      [this.threshold, this.targetPercentile],
      [this.xMax, this.targetPercentile],
    ];

    // The failure region for strain responses
    const failArea = [
      [this.threshold, this.targetPercentile],
      [this.xMax, this.targetPercentile],
    ];

    // The acceptable region for strain responses
    const goodArea = [
      [0, 100],
      [this.threshold, 100],
      [this.threshold, this.targetPercentile],
      [this.xMax, this.targetPercentile],
    ];


    const series = [
      {
        name: 'Threshold',
        data: thresholdLine,
      },
      {
        name: 'failArea',
        data: failArea,
      },
      {
        name: 'goodArea',
        data: goodArea,
        tooltip: {
          formatter: `Acceptable:<br>${this.threshold} - 0 μStrain`,
        },
      },
    ];


    if (this.binnedResults) {
      series.push({
        name: 'Deflection Result',
        data: this.binnedResults.map((r) => [r.lowerBound, (1 - r.cumulPct) * 100]),
      });
    }


    this.chart.setOption({
      series,
    });
  }
}
